/**
 * Meeting Companion (TS-1963): the contract the rest of the app opens it with.
 *
 * The companion is a notes window an account manager keeps beside a client call. The calendar
 * integration (Lucas Freitas, in progress) shows upcoming meetings on Home; its "Join" tooltip
 * calls `openMeetingCompanion(meeting)` beside opening the call. Until it lands, the launcher at
 * `/meeting-companion` opens it with sample meetings.
 *
 * 🔴 Call `openMeetingCompanion` SYNCHRONOUSLY inside the click handler. The host opens a
 * Document Picture-in-Picture (or popup) window from the event listener, and the browser only
 * allows that while the click's transient user activation is live: an `await`, a timer or a
 * state round-trip before the call spends it, and the window is refused.
 */

export interface MeetingAttendee {
  name: string | null;
  email: string;
}

export interface Meeting {
  /** Stable per meeting (the calendar event id): keys the draft, so reopening resumes it. */
  id: string;
  title: string;
  /** ISO instants. */
  start: string;
  end: string;
  /** The Meet / Zoom link, when the event has one. */
  joinUrl: string | null;
  attendees: MeetingAttendee[];
  /** The CRM project when the calendar already knows it; else found from the attendees' emails. */
  projectId?: string | null;
}

export const MEETING_COMPANION_OPEN_EVENT = 'crm:meeting-companion-open';
export const MEETING_COMPANION_CLOSE_EVENT = 'crm:meeting-companion-close';

/** Open the companion for a meeting (or switch it to that meeting when one is open). */
export function openMeetingCompanion(meeting: Meeting): void {
  window.dispatchEvent(new CustomEvent(MEETING_COMPANION_OPEN_EVENT, { detail: meeting }));
}

export function closeMeetingCompanion(): void {
  window.dispatchEvent(new CustomEvent(MEETING_COMPANION_CLOSE_EVENT));
}

/** Our own people, by email domain: never "the client" when matching a project. */
export const INTERNAL_DOMAINS = ['remoteleverage.com'];

export function isInternal(email: string): boolean {
  const domain = email.split('@')[1]?.toLowerCase() ?? '';
  return INTERNAL_DOMAINS.includes(domain);
}
